import {Utils, StringUtils, HtmlUtils, lodash} from 'neo-common';
import {ForceToolApi} from './ForceToolApi';
import {ForceToolCommand, ForceToolDefinition, ForceToolExternalSetupInfo, ForceToolSet, SetCategory, SetDefinition} from './Types';
import md5 = require('blueimp-md5');

const elementIds = {
    demoContainer: 'forceToolDemo',
    container: 'forceToolContainer',
    overlay: 'forceToolOverlay',
    demoCloseOverlay: 'forceToolDemoCloseOverlay',
    title: 'forceToolTitle',
    titleText: 'title-text',
    titleBtn: 'title-btn',
    titleActionsPanel: 'title-actions',
    forceToolDemoBtn: 'forceToolDemoBtn',
    forceToolList: 'forceToolList',
    forceToolContent: 'forceToolContent',
    currentForceSet: 'currentForceSet',
    linkContentCurrent: 'linkContentCurrent',
    linkContentAll: 'linkContentAll',

    txtCurrentForceSet: 'txtCurrentForceSet',
    txtCurrentForceSetFile: 'txtCurrentForceSetFile',

    divForceSetCommands: 'divForceSetCommands',
    divForceSetRawContent: 'divForceSetRawContent',
    txtRawForceSetCommands: 'txtRawForceSetCommands',
    divCommandsFooterNextCommand: 'divCommandsFooterNextCommand',
    divCommandsFooterLinks: 'divCommandsFooterLinks',
    lblCommandsFooterNextCommand: 'lblCommandsFooterNextCommand',
    linkCommandsFooterEdit: 'linkCommandsFooterEdit',
    linkCommandsFooterEditCancel: 'linkCommandsFooterEditCancel',
    linkCommandsFooterEditRestore: 'linkCommandsFooterEditRestore'
};

const elements = {
    demoContainer: null as HTMLDivElement,
    container: null as HTMLDivElement,
    overlay: null as HTMLDivElement,
    title: null as HTMLHeadingElement,
    titleText: null as HTMLSpanElement,
    titleBtn: null as HTMLSpanElement,
    titleActionsPanel: null as HTMLDivElement,
    forceToolDemoBtn: null as HTMLButtonElement,
    forceToolList: null as HTMLDivElement,
    forceToolContent: null as HTMLDivElement,
    demoCloseOverlay: null as HTMLDivElement,

    currentForceSet: null as HTMLDivElement,
    linkContentCurrent: null as HTMLAnchorElement,
    linkContentAll: null as HTMLAnchorElement,
    txtCurrentForceSet: null as HTMLLabelElement,
    txtCurrentForceSetFile: null as HTMLLabelElement,

    divForceSetCommands: null as HTMLDivElement,
    divForceSetRawContent: null as HTMLDivElement,
    txtRawForceSetCommands: null as HTMLTextAreaElement,
    divCommandsFooterNextCommand: null as HTMLDivElement,
    divCommandsFooterLinks: null as HTMLDivElement,
    lblCommandsFooterNextCommand: null as HTMLSpanElement,
    linkCommandsFooterEdit: null as HTMLAnchorElement,
    linkCommandsFooterEditCancel: null as HTMLAnchorElement,
    linkCommandsFooterEditRestore: null as HTMLAnchorElement
};

type ForceEditorMode = 'list' | 'editor';

const HREF_VOID = 'javascript: void(0);';

const htmlTemplates = {
    demoContainer: `<div>
    <button id="forceToolDemoBtn" class="ready"><span>DEMO</span></button>
</div>`,

    container: `<div id="forceToolOverlay" style="display: none;"></div>
<div id="forceToolDemoCloseOverlay"></div>
<div id="forceToolTitle">
<span id="title-text"></span>
<span id="title-btn"></span>
<div id="title-actions">
    <a href="${HREF_VOID}" id="linkContentCurrent">Current</a>
    <a href="${HREF_VOID}" id="linkContentAll" class="selected">All</a>
</div>
</div>
    <div id="forceToolContent">
        <div id="forceToolList"></div>
        <div id="currentForceSet" data-mode="list">
            <label class="title" id="txtCurrentForceSet"></label>
            <label class="subtitle" id="txtCurrentForceSetFile"></label>
            <div class="content" id="divForceSetCommands">
            </div>
            <div id="divForceSetRawContent">
                <textarea id="txtRawForceSetCommands"></textarea>
            </div>
            <div class="footer">
                <div id="divCommandsFooterNextCommand">
                    <label>Next command:</label>
                    <span id="lblCommandsFooterNextCommand"></span>
                </div>
                <div id="divCommandsFooterLinks">
                    <a href="${HREF_VOID}" id="linkCommandsFooterEditCancel">Cancel</a>
                    <a href="${HREF_VOID}" id="linkCommandsFooterEdit">Edit</a>
                    <a href="${HREF_VOID}" id="linkCommandsFooterEditRestore">Restore</a>
                </div>
            </div>
        </div>
    </div>`,

    categoryName: (category: SetCategory) => `<label class="category">${category.Name} (${category.Sets.length})</label>`,

    categorySetItem: (category: SetCategory, set: SetDefinition) =>
        `<li>
            <button data-category="${category.Name}" data-file="${set.File}" title="${set.Name} - ${set.Description}">
                <span class="name">${set.Name}</span>
                <span class="description">${set.Description}</span>
            </button>
        </li>`
};

const NEWLINE = '\n';

export enum ForceToolUIState {
    Enabled,
    Disabled
}

export enum ForceToolLayout {
    Debug = 'debug',
    DemoDebug = 'demodebug',
    Demo = 'demo'
}

export class ForceToolUI {
    private static _initialized = false;
    private static _state = ForceToolUIState.Enabled;
    private static _layout = ForceToolLayout.Debug;
    private static _demoRunning = false;
    private static _externalSetupInfo: ForceToolExternalSetupInfo;
    private static _pinDialogVisible: boolean;

    public static initialize(layout: ForceToolLayout, externalSetupInfo: ForceToolExternalSetupInfo): Promise<any> {
        return Promise.resolve()
            .then(() => {
                if (!ForceToolUI._initialized) {
                    ForceToolUI._externalSetupInfo = externalSetupInfo;
                    ForceToolUI._layout = layout;
                    ForceToolUI.initHtml();
                    ForceToolUI._initialized = true;
                    return ForceToolApi.getDefinition()
                        .then(definition => ForceToolUI.drawList(definition))
                        .then(() => ForceToolUI.afterDrawList())
                        .catch(err => ForceToolUI.drawError(err, 'Error loading force tool definition file'));
                }
            });
    }

    public static updateState(state: ForceToolUIState): void {
        if (state !== ForceToolUI._state) {
            ForceToolUI._state = state;
            //ForceToolUI.refresh();

            HtmlUtils.toggleShow(elements.overlay, state === ForceToolUIState.Disabled);
            setTimeout(() => {
                ForceToolUI.refresh();
            }, 10);
        }
    }

    public static get demoRunning(): boolean {
        return this._demoRunning;
    }

    public static set demoRunning(value: boolean) {
        this._demoRunning = value;
    }

    private static get layoutIsDemo(): boolean {
        return ForceToolUI._layout === ForceToolLayout.Demo;
    }

    private static get layoutIsDemoLike(): boolean {
        return Utils.valueIsIn(ForceToolUI._layout, [ForceToolLayout.Demo, ForceToolLayout.DemoDebug]);
    }

    private static get layoutIsDebugLike(): boolean {
        return Utils.valueIsIn(ForceToolUI._layout, [ForceToolLayout.Debug, ForceToolLayout.DemoDebug]);
    }

    private static initHtml() {
        // force tool container ...
        let container = document.getElementById(elementIds.container);
        if (container) {
            container.parentElement.removeChild(container);
        }

        container = document.createElement('div');
        container.setAttribute('id', elementIds.container);
        container.classList.add('expanded');
        container.innerHTML = htmlTemplates.container;
        container.dataset['layout'] = ForceToolUI._layout;

        if (ForceToolUI.layoutIsDemoLike && !ForceToolApi.hasSelectedForceSet) {
            container.style.display = 'none';
        }

        document.body.insertBefore(container, document.body.firstChild);

        // forceToolDemo
        let demoContainer = document.getElementById(elementIds.demoContainer);
        if (demoContainer) {
            demoContainer.parentElement.removeChild(demoContainer);
        }

        if (ForceToolUI.layoutIsDemoLike) {
            demoContainer = document.createElement('div');
            demoContainer.setAttribute('id', elementIds.demoContainer);
            demoContainer.innerHTML = htmlTemplates.demoContainer;
            document.body.insertBefore(demoContainer, document.body.firstChild);
        }

        ForceToolUI._pinDialogVisible = false;

        // find elements
        ForceToolUI.findElements(ForceToolUI.layoutIsDebugLike);

        if (ForceToolUI.layoutIsDemoLike) {
            const clearFix = document.createElement('div');
            clearFix.classList.add('clearfix');
            elements.demoContainer.parentNode.insertBefore(clearFix, elements.demoContainer.nextSibling);

            elements.forceToolDemoBtn.addEventListener('click', ForceToolUI.demoButtonClick);

            elements.demoCloseOverlay.addEventListener('click', ForceToolUI.demoButtonClick);
        } else {
            container.removeChild(elements.demoCloseOverlay);
        }

        elements.titleText.innerText = ForceToolUI.layoutIsDemoLike ? 'Select Demo Set' : 'Force Tool Sets';

        if (!ForceToolUI.layoutIsDebugLike) {
            HtmlUtils.toggleShow(elements.titleActionsPanel, false);
        }
        HtmlUtils.toggleShow(elements.txtCurrentForceSetFile, ForceToolUI.layoutIsDebugLike);
        HtmlUtils.toggleShow(elements.txtCurrentForceSet, ForceToolUI.layoutIsDebugLike);

        if (!ForceToolUI.layoutIsDebugLike) {
            elements.divForceSetRawContent.parentElement.removeChild(elements.divForceSetRawContent);
            //elements.divCommandsFooterNextCommand.parentElement.removeChild(elements.divCommandsFooterNextCommand);
            elements.divCommandsFooterLinks.parentElement.removeChild(elements.divCommandsFooterLinks);
        }

        // attach handlers to html events of elements
        elements.titleBtn.addEventListener('click', ForceToolUI.expandCollapse);
        elements.linkContentAll.addEventListener('click', ForceToolUI.changeContent);
        elements.linkContentCurrent.addEventListener('click', ForceToolUI.changeContent);
        elements.linkCommandsFooterEdit.addEventListener('click', ForceToolUI.editCancelRawEditor);
        elements.linkCommandsFooterEditCancel.addEventListener('click', ForceToolUI.editCancelRawEditor);
        elements.linkCommandsFooterEditRestore.addEventListener('click', ForceToolUI.editCancelRawEditor);

        const forceFileSetSample = JSON.stringify(ForceToolApi.getSampleForceSetFile(), null, '  ');
        elements.txtRawForceSetCommands.placeholder = `Enter valid force set commands JSON.\nExample:\n\n` + forceFileSetSample;

        //elements.forceToolList.innerHTML = 'Loading...';
        elements.forceToolList.innerHTML = '';
    }

    private static afterDrawList(allowedPinDetection: boolean = true): void {
        // attach click on list buttons (sets)
        const setsButtons = HtmlUtils.queryElements('#forceToolList button[data-file]') as HTMLButtonElement[];
        lodash.each(setsButtons, (btn: HTMLButtonElement) => {
            btn.addEventListener('click', ForceToolUI.openSetButtonClick);
        });

        if (ForceToolUI.layoutIsDemo) {
            ForceToolApi.clearCurrent();
        }

        ForceToolUI.redrawCurrentForceSet();

        if (allowedPinDetection) {
            const genDemoPin = ForceToolUI.generatePinHashMode;
            if (genDemoPin && ForceToolUI.layoutIsDebugLike) {
                ForceToolUI.renderPINDialog(true);
            } else {
                ForceToolUI.renderEnterPINDialog();
            }
        }

        return null;
    }

    private static get generatePinHashMode(): boolean {
        return Utils.asBool(Utils.valueOrDefault(Utils.getURIQuery()['gen.hash'], 'false'))
               && ForceToolUI.layoutIsDebugLike;
    }

    private static renderEnterPINDialog(): void {
        if (!Utils.isNullOrUndefined(this._externalSetupInfo) && ForceToolUI.layoutIsDemoLike) {
            const demoPINHash = this._externalSetupInfo.forceToolDemoPINHash();
            if (!StringUtils.isNullOrEmpty(demoPINHash)) {
                ForceToolUI.renderPINDialog(false);
            }
        }
    }

    private static renderPINDialog(generatePin: boolean): void {
        const title = generatePin ? 'Generate PIN HASH' : 'Enter DEMO PIN:';
        const submitText = generatePin ? 'Generate HASH' : 'Continue';
        const submitMode = generatePin ? 'generate' : 'validate';

        const pinPadHtml = ForceToolUI.renderNumericPINPad(!generatePin);

        /*<tr>
                    <td colspan="2"><b>${title}</b></td>
                </tr>*/

        const pinInputType = generatePin ? 'text' : 'password';

        let html = `
            <table style="font-family: initial; width: 100%;">
                
                <tr>
                    <td colspan="2">
                        <input type="${pinInputType}" id="edDemoPin" placeholder="DEMO PIN" readonly/>
                    </td>
                </tr>
                <tr>
                    <td colspan="2">
                        ${pinPadHtml}
                    </td>
                </tr>`;

        if (generatePin) {
            html += `
                <tr>
                    <td colspan="2">
                        <input type="text" readonly id="edDemoPinHash" placeholder="PIN HASH"/>
                    </td>
                </tr>`;
        }

        html += `</table>`;
        elements.forceToolContent.innerHTML = html;

        const pinPadButtons = HtmlUtils.queryElements('button[data-pin-button]');
        lodash.each(pinPadButtons, (btn: HTMLButtonElement) => {
            btn.addEventListener('click', ForceToolUI.pinPadButtonClicked);
        });

        ForceToolUI._pinDialogVisible = true;
    }

    private static pinPadButtonClicked(event: Event): void {
        const button = event.currentTarget as HTMLButtonElement;
        const btnValue = Number(button.dataset['pinButton']);
        const edDemoPin = document.getElementById('edDemoPin') as HTMLInputElement;

        let generatePinHash = ForceToolUI.layoutIsDebugLike;
        let canSubmit = ForceToolUI.generatePinHashMode;

        if (btnValue > -1) {
            edDemoPin.value += btnValue;
        } else {
            if (btnValue === -1) {
                const value = edDemoPin.value;
                if (!StringUtils.isNullOrEmpty(value) && value.length > 0) {
                    edDemoPin.value = value.length === 1 ? '' : value.substr(0, value.length - 1);
                }
            } else if (btnValue === -2) {
                generatePinHash = false;
                canSubmit = true;
            }
        }

        if (canSubmit) {
            ForceToolUI.submitDemoPinHash(generatePinHash);
        }
    }

    private static renderNumericPINPad(renderAccept: boolean = true): string {
        const acceptHtml = renderAccept ? '<td><button data-pin-button="-2">✓</button></td>' : '';
        return `
        <table style="font-family: initial;">
            <tr>
                <td><button data-pin-button="7">7</button></td>
                <td><button data-pin-button="8">8</button></td>
                <td><button data-pin-button="9">9</button></td>
            </tr>
            <tr>
                <td><button data-pin-button="4">4</button></td>
                <td><button data-pin-button="5">5</button></td>
                <td><button data-pin-button="6">6</button></td>
            </tr>
            <tr>
                <td><button data-pin-button="1">1</button></td>
                <td><button data-pin-button="2">2</button></td>
                <td><button data-pin-button="3">3</button></td>
            </tr>
            <tr>
                <td><button data-pin-button="0">0</button></td>
                <td><button data-pin-button="-1">←</button></td>
                ${acceptHtml}
            </tr>
        </table>`;
    }

    private static submitDemoPinHash(generatePinHash: boolean): void {
        const edDemoPin = document.getElementById('edDemoPin') as HTMLInputElement;
        const demoPin = edDemoPin.value;
        const hasDemoPin = !StringUtils.isNullOrEmpty(demoPin);

        if (generatePinHash) {
            const hash = hasDemoPin ? md5(demoPin, null, false) : '';

            const edDemoPinHash = document.getElementById('edDemoPinHash') as HTMLInputElement;
            edDemoPinHash.value = hash;

            if (hasDemoPin) {
                edDemoPinHash.select();
                edDemoPinHash.focus();
            }
        } else {
            const demoPINHash = ForceToolUI._externalSetupInfo.forceToolDemoPINHash();
            const hash = md5(demoPin, null, false);
            if (demoPINHash === hash) {
                ForceToolUI.initHtml();
                ForceToolApi.getDefinition()
                    .then(definition => ForceToolUI.drawList(definition))
                    .then(() => ForceToolUI.afterDrawList(false))
                    .then(() => {
                        ForceToolUI.demoButtonClick();
                    })
                    .catch(err => ForceToolUI.drawError(err, 'Error loading force tool definition file'));
            } else {
                edDemoPin.value = '';
                alert('Invalid PIN!');
            }
        }
    }

    public static refresh(): void {
        ForceToolApi.getCurrentForceSet()
            .then(forceSet => {
                const hasCurrent = !Utils.isNullOrUndefined(forceSet);
                const content = hasCurrent ? 'Current' : 'All';
                ForceToolUI.changeContentTo(content, forceSet);
                if (hasCurrent) {
                    ForceToolUI.setCurrentForceSet(forceSet);
                }
            });

    }

    private static redrawCurrentForceSet(): void {
        ForceToolApi.getCurrentForceSet()
            .then(forceSet => {
                if (!Utils.isNullOrUndefined(forceSet)) {
                    ForceToolUI.setCurrentForceSet(forceSet);
                }
            });
    }

    private static setCurrentForceSet(forceSet: ForceToolSet): boolean {
        elements.txtRawForceSetCommands.value = '';
        ForceToolUI.changeContentTo('Current', forceSet);

        elements.txtCurrentForceSet.innerText = forceSet.Description;
        elements.txtCurrentForceSet.title = `${forceSet.Description} [${forceSet.Name}] File: ${forceSet.File}`;
        elements.txtCurrentForceSetFile.innerText = ForceToolApi.getNonBaseFilePath(forceSet.File);

        elements.divForceSetCommands.innerHTML = '';

        //let html = htmlTemplates.commandsTable.begin;
        let html = `<table id="tableCommands"><thead><tr>
                <th>&nbsp;</th>
                <th>#</th>
                <th>Name</th>`;

        if (ForceToolUI.layoutIsDebugLike) {
            html += `<th>Module</th><th>Action</th>`;
        }

        html += `</tr></thead><tbody>`;

        const nextCommandIndex = ForceToolApi.getCurrentCommandIndex();
        let nextCommand: ForceToolCommand = null;
        let activeCommandIndex = -1;

        for (let i = 0; i < forceSet.Commands.length; i++) {
            const command = forceSet.Commands[i];
            const isNextCommand = i === nextCommandIndex;
            if (isNextCommand) {
                nextCommand = command;
                activeCommandIndex = i;
            }
            const nextCommandCssClass = isNextCommand ? 'current' : '';
            const nextCommandHtml = isNextCommand ? '&#10148;' : '';
            const rowCssClass = (!isNextCommand && i < nextCommandIndex) ? 'inactive' : '';
            const idx = i + 1 + '.';
            const isEmptyCommand = StringUtils.isNullOrEmpty(command.Command);

            html +=
                `<tr class="${rowCssClass}">
                    <td class="${nextCommandCssClass}">${nextCommandHtml}</td>
                    <td>${idx}</td>`;

            const cellCss = isEmptyCommand ? 'no-cmd-cell' : '';

            const commandItemTitle = isEmptyCommand ? '&lt;EMPTY COMMAND&gt;' : command.Description;
            html += `<td title="Command: ${command.Command}" class="${cellCss}">
                        <a href="${HREF_VOID}" data-command="true" data-commandempty="${isEmptyCommand}" data-commandindex="${i}">${commandItemTitle}</a>
                    </td>`;

            if (ForceToolUI.layoutIsDebugLike) {
                html += `<td class="${cellCss}">${command.Module}</td><td class="${cellCss}">${command.Action}</td>` + NEWLINE;
            }

            html += `</tr>` + NEWLINE;
        }

        //html += htmlTemplates.commandsTable.end;
        html += `</tbody></table>`;
        elements.divForceSetCommands.innerHTML = html;

        const hasNextCommand = !Utils.isNullOrUndefined(nextCommand);

        elements.lblCommandsFooterNextCommand.innerText = hasNextCommand
            ? `${nextCommandIndex + 1}. ${nextCommand.Description} (${nextCommand.Module}/${nextCommand.Action})`
            : '';

        elements.lblCommandsFooterNextCommand.title = hasNextCommand
            ? `Command: ${nextCommand.Command}`
            : '';

        const commandsLinks = HtmlUtils.queryElements('#divForceSetCommands a[data-command="true"]') as HTMLAnchorElement[];
        lodash.each(commandsLinks, (link: HTMLAnchorElement) => {
            link.addEventListener('click', ForceToolUI.changeCommandIndex);
        });

        if (activeCommandIndex > -1) {
            const activeCommandLinks = HtmlUtils.queryElements(`a[data-commandindex="${activeCommandIndex}"]`);
            if (activeCommandLinks.length > 0) {
                (activeCommandLinks[0] as any).scrollIntoView(false);
            }
        }

        return hasNextCommand;
    }

    private static demoButtonClick(): void {
        if (ForceToolUI.demoRunning) {
            return;
        }

        function toggleContainerVisible() {
            const containerVisible = elements.container.style.display === 'block';
            HtmlUtils.toggleShow(elements.container, !containerVisible);
        }

        if (ForceToolUI._pinDialogVisible) {
            return toggleContainerVisible();
        }

        const hasCurrentCssClass = elements.forceToolContent.classList.contains('current');
        //HtmlUtils.toggleShow(elements.container, true);

        if (hasCurrentCssClass) {
            HtmlUtils.toggleShow(elements.container, true);
            ForceToolUI.changeContentTo('All', null);
        } else {
            return toggleContainerVisible();
        }
    }

    private static expandCollapse(): void {
        if (ForceToolUI.layoutIsDemo) {
            ForceToolUI.demoButtonClick();
        } else {
            const elem = elements.container;
            const expanded = elem.classList.contains('expanded');
            const currentClass = expanded ? 'expanded' : 'collapsed';
            const newClass = expanded ? 'collapsed' : 'expanded';
            elem.classList.remove(currentClass);
            elem.classList.add(newClass);
        }
    }

    private static openSetButtonClick(event: Event): void {
        const self = ForceToolUI;
        const button = event.currentTarget as HTMLButtonElement;
        const category = button.dataset['category'];
        const file = button.dataset['file'];
        ForceToolApi.setCurrentCommandIndex(-1);
        ForceToolApi.getFileSet(category, file)
            .then(setFile => {
                if (!Utils.isNullOrUndefined(setFile)) {
                    const wasSet = ForceToolUI.setCurrentForceSet(setFile);
                    self.demoRunning = wasSet && self.layoutIsDemoLike;
                    if (self.demoRunning && self.layoutIsDemo) {
                        HtmlUtils.toggleShow(elements.container, false);
                        elements.forceToolDemoBtn.classList.remove('ready');
                        elements.forceToolDemoBtn.classList.add('busy');
                    }
                }
            });
    }

    public static notifyCommandSent(forceSet: ForceToolSet, commandIndex: number): void {
        let forceSetEnded = false;
        let newCommandIndex = commandIndex + 1;
        if (newCommandIndex >= forceSet.Commands.length) {
            newCommandIndex = 0;
            forceSetEnded = true;
        }

        ForceToolApi.setCurrentCommandIndex(newCommandIndex);
        ForceToolUI.refresh();

        if (forceSetEnded) {
            // when all force commands was sent and if demo is running, turn off demo
            if (ForceToolUI.demoRunning && ForceToolUI.layoutIsDemo) {
                setTimeout(() => {
                    elements.forceToolDemoBtn.classList.remove('busy');
                    elements.forceToolDemoBtn.classList.add('ready');
                    ForceToolApi.clearCurrent();

                    ForceToolUI.demoRunning = false;
                    elements.titleText.innerText = ForceToolUI.layoutIsDemoLike ? 'Select Demo Set' : 'Force Tool Sets';
                    ForceToolUI.renderEnterPINDialog();
                }, 1 * 1000);
            }
        }
    }

    private static changeCommandIndex(event: Event): void {
        const link = event.target as HTMLAnchorElement;
        const commandindex = Number(link.dataset['commandindex']);
        ForceToolApi.setCurrentCommandIndex(commandindex);
        ForceToolUI.redrawCurrentForceSet();
    }

    private static editCancelRawEditor(event: Event): void {
        const link = event.target as HTMLAnchorElement;
        const clickedEdit = link.id === elementIds.linkCommandsFooterEdit;
        const currentMode = elements.currentForceSet.dataset['mode'] as ForceEditorMode;

        function setNewMode(newMode: ForceEditorMode) {
            elements.currentForceSet.dataset['mode'] = newMode;
            elements.linkCommandsFooterEdit.innerText = newMode === 'editor' ? 'Apply' : 'Edit';
        }

        if (clickedEdit) {
            if (currentMode === 'list') {
                setNewMode('editor');

                if (StringUtils.isNullOrEmpty(elements.txtRawForceSetCommands.value)) {
                    ForceToolApi.getCurrentForceSet()
                        .then(forceSet => {
                            if (!Utils.isNullOrUndefined(forceSet)) {
                                const json = lodash.cloneDeep(forceSet) as ForceToolSet;
                                delete json.File;

                                elements.txtRawForceSetCommands.value = JSON.stringify(json, null, '  ');
                            }
                        });
                }
            } else {
                let forceSet: ForceToolSet = null;
                try {
                    forceSet = StringUtils.isNullOrEmpty(elements.txtRawForceSetCommands.value) ? null : JSON.parse(elements.txtRawForceSetCommands.value);
                } catch (err) {
                    alert('Error parsing text, its not valid JSON!');
                }

                if (forceSet) {
                    if (!ForceToolApi.isValidForceSetFile(forceSet)) {
                        alert('Invalid file format!');
                    } else {
                        ForceToolApi.updateCurrentForceSet(forceSet)
                            .then(success => {
                                if (success) {
                                    setNewMode('list');
                                    ForceToolUI.setCurrentForceSet(forceSet);
                                }
                            });
                    }
                }
            }
        } else {
            const clickedRestore = link.id === elementIds.linkCommandsFooterEditRestore;
            if (clickedRestore) {
                if (confirm('Do you want to restore editor with definition from file?')) {
                    ForceToolApi.resetCurrentForceSet()
                        .then(forceSet => {
                            if (forceSet) {
                                ForceToolUI.setCurrentForceSet(forceSet);
                                const json = lodash.cloneDeep(forceSet) as ForceToolSet;
                                delete json.File;

                                elements.txtRawForceSetCommands.value = JSON.stringify(json, null, '  ');
                            }
                        });
                }
            } else {
                const hasSomeData = !StringUtils.isNullOrEmpty(elements.txtRawForceSetCommands.value) && elements.txtRawForceSetCommands.value.length > 0;

                if (hasSomeData) {
                    if (!confirm('Do you want to keep current changes in editor?\n' +
                                 'OK - Keep editor text\n' +
                                 'Cancel - Clear editor text')) {
                        elements.txtRawForceSetCommands.value = '';
                    }
                }
                setNewMode('list');
            }
        }
    }

    private static changeContent(event: Event): void {
        const link = event.target as HTMLAnchorElement;
        const clickedAll = (link.id === elementIds.linkContentAll);
        if (clickedAll) {
            ForceToolUI.changeContentTo('All', null);
        } else {
            ForceToolApi.getCurrentForceSet()
                .then(forceSet => {
                    ForceToolUI.changeContentTo('Current', forceSet);
                });
        }
    }

    private static changeContentTo(content: 'Current' | 'All', forceSet: ForceToolSet): void {
        const changeToAll = content === 'All';
        const hasCurrentCssClass = elements.forceToolContent.classList.contains('current');

        if (changeToAll) {
            ForceToolApi.clearCurrent();

            if (hasCurrentCssClass) {
                elements.forceToolContent.classList.remove('current');
            }

            if (elements.linkContentCurrent.classList.contains('selected')) {
                elements.linkContentCurrent.classList.remove('selected');
            }

            if (!elements.linkContentAll.classList.contains('selected')) {
                elements.linkContentAll.classList.add('selected');
            }

            elements.titleText.innerText = ForceToolUI.layoutIsDemoLike ? 'Select Demo Set' : 'Force Tool Sets';
        } else {
            if (!hasCurrentCssClass) {
                elements.forceToolContent.classList.add('current');
            }

            if (elements.linkContentAll.classList.contains('selected')) {
                elements.linkContentAll.classList.remove('selected');
            }

            if (!elements.linkContentCurrent.classList.contains('selected')) {
                elements.linkContentCurrent.classList.add('selected');
            }

            //elements.titleText.innerText = ForceToolUI.layoutIsDemoLike ? 'Demo Set' : 'Force Tool Set';
            elements.titleText.innerText = forceSet ? forceSet.Description : '-';
        }
    }

    private static findElements(layoutIsDebugLike: boolean): boolean {
        let hasElements = true;

        for (const key in elementIds) {
            const elementId = elementIds[key];
            elements[key] = document.getElementById(elementId);

            if (Utils.isNullOrUndefined(elements[key])) {
                if (layoutIsDebugLike && Utils.valueIsIn(elementId, [elementIds.demoContainer, elementIds.forceToolDemoBtn])) {
                    continue;
                }

                hasElements = false;
                if (DEBUG) {
                    console.error(`Could not find element with id '${elementId}' `);
                }
                break;
            }
        }

        return hasElements;
    }

    private static drawList(definition: ForceToolDefinition | undefined): void {
        let html = '';
        for (let i = 0; i < definition.Categories.length; i++) {
            const category = definition.Categories[i];
            html += htmlTemplates.categoryName(category) + NEWLINE;
            html += '<ul>' + NEWLINE;

            if (category.Sets.length === 0) {
                html += '<li>CATEGORY HAS NO SETS</li>' + NEWLINE;
            } else {
                for (let j = 0; j < category.Sets.length; j++) {
                    const set = category.Sets[j];
                    html += htmlTemplates.categorySetItem(category, set) + NEWLINE;
                }
            }

            html += '</ul>' + NEWLINE;
        }

        elements.forceToolList.innerHTML = html;

        return null;
    }

    private static drawError(error: any, message: string) {
        elements.forceToolList.innerHTML = message;

        if (error) {
            console.error(error);
        }
    }
}


// WEBPACK FOOTER //
// ./src/ForceToolUI.ts